import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";

import { getLocaleText } from "../actions/datagrid";
import { contextMenu, defaultColDef, sideBar, statusBar, gridSettings } from "./grid";
import { useDispatch, useSelector } from "react-redux";
import { buildAlertWithLocalizedMessage } from "../actions";
import { PdfReport } from "./pdfreport";
import { authSelector } from "../reducers";
import { localized } from "./utils";
import { checkEmployeesGet } from "../api";
import { Button } from "./Button.tsx";

const title = "Palkkatietojen vertailu ePalkat - HR";
let isFetching = false;

// move array item to beginning of array
function moveToStart(arr, item) {
  const idx = arr.indexOf(item); // find item index
  if (idx === -1) return; // not found
  const removed = arr.splice(idx, 1)[0]; // remove from arr
  arr.unshift(removed); // add to beginning
}

function allCols(data) {
  // collect all unique keys from data to generate column headers
  const allKeys = {};
  if (data !== null) {
    for (const item of data) {
      for (const key in item) {
        allKeys[key] = true;
      }
    }
  }

  const cols = Object.keys(allKeys).sort();
  moveToStart(cols, "onlyOnHr");
  moveToStart(cols, "onlyOnPalkat");
  moveToStart(cols, "differs");
  moveToStart(cols, "name");
  moveToStart(cols, "nr");
  return cols;
}

export function ComparePersons() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const gridRef = useRef();

  const { companyName } = useSelector(authSelector);

  const [report, setReport] = useState(null);
  const closeReport = () => setReport(null);

  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState(null);

  const autoColumnDefs =
    data &&
    allCols(data).map((field) => {
      const colDef = { field };

      switch (field) {
        case "nr":
          colDef.pinned = "left";
          break;
        case "name":
          colDef.filter = "agTextColumnFilter";
          break;

        case "differs":
        case "onlyOnPalkat":
        case "onlyOnHr":
          colDef.cellDataType = "boolean";
          colDef.filter = "agSetColumnFilter";
          break;

        case "employmentTypeHr":
        case "employmentTypePalkat":
          colDef.valueGetter = (params) => localized.employmentTypes[params.data[field]] || params.data[field];
          colDef.filter = "agSetColumnFilter";
          break;

        case "formOfSalaryHr":
        case "formOfSalaryPalkat":
          colDef.valueGetter = (params) => localized.salaryForms[params.data[field]] || params.data[field];
          colDef.filter = "agSetColumnFilter";
          break;

        case "personalBenefit1Hr":
        case "personalBenefit1Palkat":
        case "personalBenefit2Hr":
        case "personalBenefit2Palkat":
          colDef.valueGetter = (params) => localized.personalBenefit[params.data[field]] || params.data[field];
          colDef.filter = "agSetColumnFilter";
          break;
      }

      return colDef;
    });

  async function update() {
    if (!isFetching) {
      setFetching((isFetching = true));
      try {
        const res = await checkEmployeesGet();
        setData(res.data);
      } catch (err) {
        console.error(err);
        dispatch(buildAlertWithLocalizedMessage("ERROR", "StatisticsFetchError", "Virhe tilaston haussa"));
      } finally {
        setFetching((isFetching = false));
      }
    }
  }

  useEffect(() => {
    if (!data) {
      update();
    }
  }, []);

  const getContextMenuItems = useCallback((params) => {
    return contextMenu(t, params, setReport, {
      title,
      companyname: companyName,
    });
  }, []);

  return (
    <>
      {report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
      <Row className="my-1">
        <Col sm="4">
          <h3>{title}</h3>
        </Col>
        <Col sm="8" align="right">
          <Button variant="primary" disabled={fetching} onClick={update}>
            {t("Update", "Päivitä")}
          </Button>
        </Col>
      </Row>
      <Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
        <AgGridReact
          {...gridSettings}
          loading={fetching}
          ref={gridRef}
          columnDefs={autoColumnDefs}
          defaultColDef={{ ...defaultColDef, filter: "agNumberColumnFilter" }}
          rowClassRules={{ endedRow: ({ data }) => data?.differs }}
          sideBar={sideBar}
          rowData={data}
          getContextMenuItems={getContextMenuItems}
          statusBar={statusBar}
          getLocaleText={(params) => getLocaleText(t, params)}
          animateRows
          enableCellTextSelection
          suppressCellFocus
          suppressColumnVirtualisation // https://github.com/ag-grid/ag-grid/issues/1692
          onFirstDataRendered={(params) => params.api.autoSizeAllColumns()}
          //onRowDataUpdated={params => params.api.sizeColumnsToFit()}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
}
